import { Injectable, ApplicationRef, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, first, interval, concat, fromEvent, race, timer } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AppUpdateToastComponent } from '@components/notifications/app-update-toast/app-update-toast.component';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private swUpdate = inject(SwUpdate);
  private applicationRef = inject(ApplicationRef);
  private toastr = inject(ToastrService);

  public init(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    // Wait for app stability, but fall back after 5 s so that long-running
    // intervals or timers (which keep Angular "unstable") don't prevent
    // update checks from ever starting.
    const appIsStableOrTimeout$ = race(
      this.applicationRef.isStable.pipe(first(isStable => isStable === true)),
      timer(5000)
    );

    // Check for updates every 30 seconds once the app is ready
    const everyThirtySecondsOnceReady$ = concat(appIsStableOrTimeout$, interval(30 * 1000));

    everyThirtySecondsOnceReady$.subscribe(() => {
      this.swUpdate.checkForUpdate();
    });

    // Also check immediately when the user returns to a background tab —
    // browser throttles setInterval for background tabs, so this covers
    // users who were away for a long time.
    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'))
      .subscribe(() => this.swUpdate.checkForUpdate());

    // Listen for version updates
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe(() => {
        this.promptUserToUpdate();
      });

    // If the service worker enters an unrecoverable state (e.g. cached
    // assets are missing from the server), reload to recover cleanly.
    this.swUpdate.unrecoverable.subscribe(() => {
      window.location.reload();
    });
  }

  private promptUserToUpdate(): void {
    this.toastr.show('', '', {
      toastComponent: AppUpdateToastComponent,
      timeOut: 0,
      extendedTimeOut: 0,
      closeButton: false,
      tapToDismiss: false,
      positionClass: 'toast-top-right',
      toastClass: 'ngx-toastr app-update-custom-toast'
    });
  }
}
